import { Alert } from '@components/display/alerts';
import { Button } from '@components/utility/buttons';
import { FormControlCheckbox } from '@components/utility/checkboxes';
import { FileUpload } from '@components/utility/files';
import { InputField } from '@components/utility/inputs';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { AlertTitle, Divider, FormHelperText, Grid2, Typography } from '@mui/material';
import { PinPukRecoveryForm, RLAFormMode } from '@rla/types';
import { useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

export const RLAFormFooter = ({ mode }: { mode: RLAFormMode }) => {
    const formik = useFormikContext<PinPukRecoveryForm>();
    const { values, isValidating, isValid, handleBlur, errors, touched, isSubmitting } = formik;
    const intl = useIntl();

    return (
        <>
            <Grid2 size={12}>
                <Typography fontFamily="museo-sans" fontSize={16} fontWeight={600} mb="10px" color="neutral-900">
                    <FormattedMessage id="rla.documentationUpload" defaultMessage="Documentation Upload" />
                    <Typography component="span" color="signal-warning">
                        {' '}
                        *
                    </Typography>
                </Typography>
                <FileUpload.Vertical
                    fileFieldName="documents"
                    formik={formik}
                    resetOnDrop={false}
                    error={!!errors.documents && !!touched.documents}
                    helperText={
                        (errors.documents as string) ?? (
                            <FormattedMessage
                                id="rla.documentationUpload.helperText"
                                defaultMessage="Ensure the photo is focused and well-lit"
                            />
                        )
                    }
                    disabled={isSubmitting}
                    fileDropzoneAreaProps={{
                        multiple: true,
                        maxFileSize: 10,
                        dropzoneClasses: '!py-[70px]',
                        icon: null,
                        titleProps: {
                            variant: 'inherit',
                            fontSize: 14,
                            fontWeight: 400,
                            letterSpacing: -0.15,
                            color: '#6A7282',
                        },
                        title: (
                            <FormattedMessage
                                id="fileUpload.title"
                                defaultMessage="<a>Click to upload</a> or drag & drop"
                                values={{
                                    a: (chunks) => (
                                        <Typography variant="inherit" component="span" color="primary" fontWeight={500}>
                                            {chunks}
                                        </Typography>
                                    ),
                                }}
                            />
                        ),
                        subTextProps: {
                            variant: 'inherit',
                            fontSize: 12,
                            fontWeight: 400,
                            color: '#99A1AF',
                        },
                        subText: (
                            <FormattedMessage
                                id="fileUpload.acceptedMedia"
                                defaultMessage="Accepted formats: JPG, GIF, PNG, PDF. Max file size: {size}MB."
                                values={{ size: 10 }}
                            />
                        ),
                        maxFileSizeInSubtext: false,
                        onBlur: handleBlur,
                        fileFieldName: 'documents',
                        dragActiveTitle: (
                            <FormattedMessage id="fileUpload.dragActive" defaultMessage="Drop files here..." />
                        ),
                    }}
                    fileListProps={{
                        pt: values.documents?.length > 0 ? 4 : 0,
                        sx: {
                            '& > div': {
                                pl: 2,
                            },
                        },
                        deleteIcon: {
                            color: 'error',
                            children: <DeleteOutlinedIcon />,
                            size: 'small',
                            sx: {
                                border: 1,
                            },
                        },
                    }}
                />
                {formik.values.documents.length > 0 && <Divider className="mt-4" />}
            </Grid2>
            <Grid2 size={12}>
                <InputField
                    buffered
                    fullWidth
                    multiline
                    minRows={3}
                    id="comment"
                    name="comment"
                    placeholder={intl.formatMessage({
                        id: 'common.comment.placeholder',
                        defaultMessage: 'Any additional comments? Leave them here.',
                    })}
                    topLabel={intl.formatMessage({ id: 'common.comment', defaultMessage: 'Additional Comments' })}
                    debounceMs={0}
                />
            </Grid2>
            {mode === RLAFormMode.VRM_ADMIN_ACCESS && (
                <Grid2 size={12}>
                    <Alert.Warning>
                        <AlertTitle>
                            <FormattedMessage
                                id="rla.vrm.adminRightsAndStakeholderNotification"
                                defaultMessage="Admin Rights And Stakeholder Notification"
                            />
                        </AlertTitle>
                        <FormattedMessage
                            id="rla.vrm.adminRightsAndStakeholderNotification.description"
                            defaultMessage="Once approved, the requested account will be added as an Admin. This user will have the authority to add or remove
                         other users and change the site name. {br} As is standard procedure, your local Victron dealer or VE Sales Manager for your area 
                         may be notified of this request.{br} Please allow up to 48 hours for your request to be processed."
                            values={{
                                br: (
                                    <>
                                        <br />
                                        <br />
                                    </>
                                ),
                            }}
                        />
                    </Alert.Warning>
                </Grid2>
            )}
            <Grid2 size={12} direction="column">
                <FormControlCheckbox
                    id="confirm_truth"
                    required
                    labelProps={{
                        label: (
                            <FormattedMessage
                                id="rla.truthfulConfirmation"
                                defaultMessage="I confirm that I answered this form truthfully."
                            />
                        ),
                        sx: {
                            display: 'flex',
                            pl: 0,
                        },
                        classes: {
                            label: '!ml-0',
                        },
                    }}
                    {...formik.getFieldProps({ name: 'confirm_truth', type: 'checkbox' })}
                    disabled={isSubmitting}
                />
                {touched.confirm_truth && (
                    <FormHelperText error={!!errors.confirm_truth} className="mt-0">
                        {errors.confirm_truth}
                    </FormHelperText>
                )}
                <FormControlCheckbox
                    required
                    id="confirm_intent"
                    labelProps={{
                        label:
                            mode === RLAFormMode.PIN_PUK_RECOVERY ? (
                                <FormattedMessage
                                    id="rla.pinpuk.informationUsageConfirmation"
                                    defaultMessage="I understand that this information is used solely for security verification and PUK recovery."
                                />
                            ) : (
                                <FormattedMessage
                                    id="rla.vrm.informationUsageConfirmation"
                                    defaultMessage="I understand that this information is used solely for security verification and VRM recovery."
                                />
                            ),
                        sx: {
                            display: 'flex',
                            pl: 0,
                        },
                        classes: {
                            label: '!ml-0',
                        },
                    }}
                    {...formik.getFieldProps({ name: 'confirm_intent', type: 'checkbox' })}
                    disabled={isSubmitting}
                />
                {touched.confirm_intent && (
                    <FormHelperText error={!!errors.confirm_intent} className="mt-0">
                        {errors.confirm_intent}
                    </FormHelperText>
                )}
            </Grid2>
            <Grid2 size={12} display="flex" justifyContent="flex-end">
                <Button
                    fullRounded
                    type="submit"
                    disabled={!isValid || isSubmitting || isValidating}
                    loading={isSubmitting}
                    loadingPosition="start"
                >
                    <FormattedMessage id="common.submit.lowercase" defaultMessage="Submit" />
                </Button>
            </Grid2>
        </>
    );
};
